import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { label } from "@/lib/constants";
import { useWorkMeta } from "@/lib/workMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Loader2 } from "lucide-react";

const STATUSES = ["unassigned", "scheduled", "in_progress", "completed"];

export default function JobEditDialog({ job, onSaved }) {
  const meta = useWorkMeta();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cleaners, setCleaners] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    cleaner_ids: job.cleaner_ids || (job.cleaner_id ? [job.cleaner_id] : []),
    work_type: job.work_type,
    title: job.title,
    cost: job.cost || 0,
    scheduled_date: job.scheduled_date,
    scheduled_time: job.scheduled_time,
    priority: job.priority || "normal",
    status: job.status,
    notes: job.notes || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) api.get("/users", { params: { role: "assignable" } }).then(({ data }) => setCleaners(data));
  }, [open]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put(`/jobs/${job._id}`, { ...form, cleaner_ids: form.cleaner_ids });
      toast.success("Job updated");
      onSaved(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save job");
    }
    setBusy(false);
  };

  const remove = async (series) => {
    try {
      await api.delete(`/jobs/${job._id}`, { params: series ? { series: true } : {} });
      toast.success(series ? "Repeating jobs deleted" : "Job deleted");
      navigate("/schedule");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete job");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="edit-job-btn" variant="outline" className="border-royal/30 text-royal hover:bg-royal/5 gap-2">
          <Pencil className="h-4 w-4" /> Edit job
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-royal-deep">Edit job</DialogTitle>
          <DialogDescription>Update the crew, date, status or instructions for this job.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4" data-testid="job-edit-form">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">
              Assigned crew ({form.cleaner_ids.length})
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Tick everyone working this job. The first person is the lead. All of them get the text and push.
            </p>
            <div className="mt-2 max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-border p-2"
                 data-testid="edit-job-crew-list">
              {cleaners.map((c) => {
                const on = form.cleaner_ids.includes(c._id);
                return (
                  <label key={c._id} data-testid={`edit-job-crew-${c._id}`}
                         className="flex cursor-pointer items-center gap-2.5 rounded px-1.5 py-1 hover:bg-royal/5">
                    <input type="checkbox" checked={on} className="h-4 w-4 accent-[#1b2a63]"
                           onChange={() => set("cleaner_ids", on
                             ? form.cleaner_ids.filter((x) => x !== c._id)
                             : [...form.cleaner_ids, c._id])} />
                    <span className="text-sm text-royal-deep">{c.name}</span>
                    {on && form.cleaner_ids[0] === c._id && (
                      <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-gold">Lead</span>
                    )}
                  </label>
                );
              })}
              {!cleaners.length && <p className="text-xs text-muted-foreground">No assignable people yet.</p>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Job type</Label>
              <Select value={form.work_type} onValueChange={(v) => set("work_type", v)}>
                <SelectTrigger data-testid="edit-job-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(meta?.work_types || []).map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`edit-work-type-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="edit-job-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`edit-job-status-option-${s}`}>{label(s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Date</Label>
              <Input data-testid="edit-job-date-input" type="date" value={form.scheduled_date} onChange={(e) => set("scheduled_date", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Start time</Label>
              <Input data-testid="edit-job-time-input" type="time" value={form.scheduled_time} onChange={(e) => set("scheduled_time", e.target.value)} className="mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Priority</Label>
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="edit-job-priority-select" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["low", "normal", "high", "urgent"].map((p) => (
                  <SelectItem key={p} value={p} data-testid={`edit-job-priority-option-${p}`}>{label(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Instructions for the crew</Label>
            <Textarea data-testid="edit-job-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1.5" />
          </div>
          <Button data-testid="save-job-edit-btn" type="submit" disabled={busy} className="w-full bg-royal hover:bg-royal-deep text-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </Button>
          <div className="flex gap-2 pt-2 border-t border-border">
            <Button
              data-testid="delete-job-btn"
              type="button"
              variant="outline"
              onClick={() => remove(false)}
              className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/5 gap-2"
            >
              <Trash2 className="h-4 w-4" /> Delete this job
            </Button>
            {job.series_id && (
              <Button
                data-testid="delete-series-btn"
                type="button"
                variant="outline"
                onClick={() => remove(true)}
                className="flex-1 border-destructive/40 text-destructive hover:bg-destructive/5"
              >
                Delete all repeats
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
